import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Tooltip } from '@mantine/core';
import { clampHealth, getHealthIndicatorColor } from '../../helpers/color-helpers/color-helpers';
import { getHealthScoreGrade } from '../../helpers/project-metrics-helper/project-metrics-helper';
import { newCOLORS } from '../../styles/colors';

type HorizontalHealthBarProps = {
  heading: string;
  tooltip: string;
  health: number;
  showNumber?: boolean;
};

export function HorizontalHealthBar({ heading, tooltip, health, showNumber = false }: HorizontalHealthBarProps) {
  const displayValue = showNumber ? health : getHealthScoreGrade(health);

  return (
    <HorizontalBarContainer>
      <GradeSection>
        <GradeText>{displayValue}</GradeText>
      </GradeSection>

      <HorizontalBarWrapper>
        <SegmentedHealthBar>
          <HealthSegment color={newCOLORS.red2} isActive={clampHealth(health) > 0} />
          <HealthSegment color={newCOLORS.orangeRed2} isActive={clampHealth(health) > 20} />
          <HealthSegment color={newCOLORS.orange2} isActive={clampHealth(health) > 40} />
          <HealthSegment color={newCOLORS.yellow2} isActive={clampHealth(health) > 60} />
          <HealthSegment color={newCOLORS.green2} isActive={clampHealth(health) > 80} />
        </SegmentedHealthBar>
        <IndicatorDot
          style={{
            left: `${clampHealth(health)}%`,
            backgroundColor: newCOLORS.white,
            borderColor: getHealthIndicatorColor(clampHealth(health)),
            boxShadow: `0 2px 4px rgba(0, 0, 0, 0.1)`,
          }}
        />
      </HorizontalBarWrapper>

      <Tooltip multiline w={300} position="top" label={tooltip}>
        <HealthBarText>
          {heading} <Icon icon="mdi:information-outline" width={16} height={16} color={newCOLORS.blue} />
        </HealthBarText>
      </Tooltip>
    </HorizontalBarContainer>
  );
}

const HorizontalBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px;
`;

const GradeSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 8px;
`;

const GradeText = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: ${newCOLORS.black2};
  line-height: 1;
  letter-spacing: -0.02em;
`;

const HealthBarText = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 600;
  color: ${newCOLORS.black2};
  margin-bottom: 12px;
`;

const HorizontalBarWrapper = styled.div`
  position: relative;
  width: 440px;
  height: 6px;
`;

const SegmentedHealthBar = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  gap: 2px;
  border-radius: 6px;
  overflow: hidden;
`;

const HealthSegment = styled.div<{ color: string; isActive: boolean }>`
  flex: 1;
  height: 100%;
  background-color: ${(props) => props.color};
  border-radius: 6px;
  opacity: ${(props) => (props.isActive ? 1 : 0.3)};
`;

const IndicatorDot = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;
